import './ProfilePicture.scss';

import { useCallback, useRef, useState } from 'react';

import AvatarEditor from 'react-avatar-editor';
import { Button, Loader } from '@trackman/web-shared-components';
import { ReactComponent as CameraIcon } from 'images/icons/Camera.svg';
import { Modal } from '@trackman/web-shared-components';
import { Slider } from '@mui/material';
import { ReactComponent as UploadIcon } from 'images/icons/Download.svg';
import { getUserInitials } from 'utilities/userInfo';
import { useServices, useStores } from 'index';
import { useTranslation } from 'react-i18next';
import { useGetProfile } from 'queryHooks/profile';
import { ProfileData, ProfilePictureDataType } from 'types/profile';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';

export const ProfilePicture = () => {
  const { t } = useTranslation();
  const profileService = useServices().profileService;
  const queryClient = useQueryClient();
  const notificationStore = useStores().notificationStore;

  const { data: profileData, isPending: isProfileDataPending } = useGetProfile();

  const [isModalOn, setIsModalOn] = useState(false);
  const inputPic = useRef<HTMLInputElement>(null);
  const [newProfilePic, setNewProfilePic] = useState<File>();
  const [profilePicZoom, setProfilePicZoom] = useState<number>(1.2);
  const [profilePicRotation, setProfilePicRotation] = useState<number>(0);
  const [newPicture, setNewPicture] = useState<ProfilePictureDataType>({
    id: '',
    fileUrl: '',
    mimeType: '',
    data: null,
  });
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  let outputPic: AvatarEditor | null = null;

  const loadNewPic = () => {
    if (inputPic.current?.files?.[0]) {
      setNewPicture({
        id: '',
        fileUrl: inputPic.current?.files?.[0].name,
        mimeType: inputPic.current?.files?.[0].type,
        data: null,
      });
    }

    setNewProfilePic(inputPic.current?.files?.[0]);
    setProfilePicZoom(1.2);
    setProfilePicRotation(0);
  };

  const closeModal = () => {
    setIsModalOn(false);
    setNewProfilePic(undefined);
    setProfilePicZoom(1.2);
    setProfilePicRotation(0);
  };

  const saveProfilePic = async () => {
    if (!outputPic) {
      return;
    }

    const canvasScaled = outputPic.getImageScaledToCanvas();
    const croppedImg = canvasScaled.toDataURL();

    await processProfilePicAndUpdateProfile(croppedImg);
    closeModal();
  };

  const processProfilePicAndUpdateProfile = async (pictureDataUrl: string) => {
    setIsUploadingImage(true);
    try {
      await profileService
        .processProfilePicAndUpdateProfile(pictureDataUrl)
        .then((response) => {
          const blob = new Blob([response], { type: response.type });

          const modifiedBlob = new Blob([blob], { type: blob.type });
          modifiedBlob['lastModified'] = new Date().getTime();

          const picFile = new File([blob], newPicture.fileUrl, {
            type: newPicture.mimeType,
          });

          const updatedPictureData: ProfilePictureDataType = {
            id: '',
            fileUrl: picFile.name,
            mimeType: picFile.type,
            data: picFile,
          };
          return updatedPictureData;
        })
        .then((updatedPictureData) => uploadProfilePicAndUpdateProfile.mutate(updatedPictureData));
    } catch {
      notificationStore.addToast('error', i18next.t('ErrorWhileConvertingProfilePic', { ns: 'editProfile' }), 'error');
    }
  };

  const uploadProfilePicAndUpdateProfile = useMutation({
    mutationFn: (updatedPictureData: ProfilePictureDataType) => profileService.uploadProfilePicAndUpdateProfile(updatedPictureData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profileData'] });
    },
    onError: (ex) => notificationStore.addToast('error', i18next.t('ErrorWhileUploadingProfilePic', { ns: 'editProfile' }), 'error'),
    onSettled: () => setIsUploadingImage(false),
  });

  return (
    <>
      <ProfilePictureContainer isUploadingImage={isUploadingImage} profileData={profileData as ProfileData} />
      <div className='profile-picture-change-icon' onClick={() => setIsModalOn(true)}>
        <CameraIcon />
      </div>
      <Modal
        title={t('UploadPicture', { ns: 'editProfile' })}
        titleIcon={<UploadIcon />}
        open={isModalOn}
        onClose={() => setIsModalOn(false)}
        aria-labelledby={t('UploadPicture', { ns: 'editProfile' })}
        aria-describedby={t('UploadPicture', { ns: 'editProfile' })}
        className='profile-pic-modal'
        cancelBtn={{
          label: t('Cancel', { ns: 'general' }),
          onClick: () => setIsModalOn(false),
          className: 'styled-button secondary',
        }}
        confirmBtn={{
          label: t('Save', { ns: 'general' }),
          onClick: () => saveProfilePic(),
        }}
      >
        <div className='modal-body'>
          {inputPic.current?.files?.[0] ? (
            <div className='profile-pic-modal-content'>
              <AvatarEditor
                ref={(e) => (outputPic = e)}
                image={newProfilePic ?? newPicture.fileUrl}
                width={162}
                height={162}
                border={13}
                color={[0, 0, 0, 0.3]}
                scale={profilePicZoom}
                borderRadius={81}
                rotate={profilePicRotation}
              />
              <div className='profile-pic-slider-container'>
                <div className='slider-value-label'>
                  {t('Zoom', { ns: 'editProfile' })} {profilePicZoom}
                </div>
                <Slider
                  value={profilePicZoom}
                  onChange={(e, zoom) => {
                    if (typeof zoom === 'number') setProfilePicZoom(zoom);
                  }}
                  valueLabelDisplay='off'
                  step={0.1}
                  min={1}
                  max={3}
                />
              </div>
              <div className='profile-pic-slider-container'>
                <div className='slider-value-label'>
                  {t('Rotation', { ns: 'editProfile' })} {profilePicRotation}°
                </div>
                <Slider
                  value={profilePicRotation}
                  onChange={(e, rotation) => {
                    if (typeof rotation === 'number') setProfilePicRotation(rotation);
                  }}
                  valueLabelDisplay='off'
                  step={1}
                  min={-30}
                  max={30}
                />
              </div>
            </div>
          ) : (
            <></>
          )}
          <div className='change-picture-container'>
            <input type='file' id='file' accept='image/*' ref={inputPic} style={{ display: 'none' }} onChange={loadNewPic} />
            <Button
              className='styled-button secondary'
              label={t('ChooseFile', { ns: 'editProfile' })}
              intent='secondary'
              onClick={() => inputPic.current?.click()}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

const ProfilePictureContainer = ({ isUploadingImage, profileData }: { isUploadingImage: boolean; profileData: ProfileData }) => {
  const { t } = useTranslation();
  return (
    <div className='profile-picture-container'>
      {isUploadingImage ? (
        <div className='loader-wrapper'>
          <Loader size='large' />
        </div>
      ) : (
        <></>
      )}
      {profileData?.pictureUrl && profileData.pictureUrl !== '/Frontend/Images/no_avatar.svg' ? (
        <img className='profile-picture' src={profileData.pictureUrl} alt={t('ProfilePicture', { ns: 'editProfile' })} />
      ) : (
        <div className='profile-picture-initials'>{profileData && getUserInitials(profileData.firstName, profileData.lastName)}</div>
      )}
    </div>
  );
};
